import { useEffect, useRef, useState } from 'react';
import interact from 'interactjs';

export const useInteract = (canvasRef, elements, setElements, addElement) => {
    const [alignmentGuides, setAlignmentGuides] = useState([]);
    const elementRefs = useRef({});
    const interactInitialized = useRef(new Set());
    const elementsRef = useRef(elements);

    // Keep elementsRef in sync with elements
    useEffect(() => {
        elementsRef.current = elements;
    }, [elements]);

    const SNAP_THRESHOLD = 5;

    const dragMoveListener = (event) => {
        const target = event.target;
        let x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
        let y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;

        const id = parseInt(target.dataset.id);
        const currentElements = elementsRef.current;
        const currentElement = currentElements.find(el => el.id === id);
        if (!currentElement) return;

        const width = parseFloat(target.style.width) || currentElement.width;
        const height = parseFloat(target.style.height) || currentElement.height;

        // Calculer les guides d'alignement
        const guides = [];
        const otherElements = currentElements.filter(el => el.id !== id);

        otherElements.forEach(other => {
            const otherLeft = other.x;
            const otherRight = other.x + other.width;
            const otherCenterX = other.x + other.width / 2;
            const otherTop = other.y;
            const otherBottom = other.y + other.height;
            const otherCenterY = other.y + other.height / 2;

            const currentLeft = x;
            const currentRight = x + width;
            const currentCenterX = x + width / 2;
            const currentTop = y;
            const currentBottom = y + height;
            const currentCenterY = y + height / 2;

            // Alignement vertical (gauche, centre, droite)
            if (Math.abs(currentLeft - otherLeft) < SNAP_THRESHOLD) {
                x = otherLeft;
                guides.push({ type: 'vertical', position: otherLeft });
            } else if (Math.abs(currentRight - otherRight) < SNAP_THRESHOLD) {
                x = otherRight - width;
                guides.push({ type: 'vertical', position: otherRight });
            } else if (Math.abs(currentCenterX - otherCenterX) < SNAP_THRESHOLD) {
                x = otherCenterX - width / 2;
                guides.push({ type: 'vertical', position: otherCenterX });
            } else if (Math.abs(currentLeft - otherRight) < SNAP_THRESHOLD) {
                x = otherRight;
                guides.push({ type: 'vertical', position: otherRight });
            } else if (Math.abs(currentRight - otherLeft) < SNAP_THRESHOLD) {
                x = otherLeft - width;
                guides.push({ type: 'vertical', position: otherLeft });
            }

            // Alignement horizontal (haut, centre, bas)
            if (Math.abs(currentTop - otherTop) < SNAP_THRESHOLD) {
                y = otherTop;
                guides.push({ type: 'horizontal', position: otherTop });
            } else if (Math.abs(currentBottom - otherBottom) < SNAP_THRESHOLD) {
                y = otherBottom - height;
                guides.push({ type: 'horizontal', position: otherBottom });
            } else if (Math.abs(currentCenterY - otherCenterY) < SNAP_THRESHOLD) {
                y = otherCenterY - height / 2;
                guides.push({ type: 'horizontal', position: otherCenterY });
            } else if (Math.abs(currentTop - otherBottom) < SNAP_THRESHOLD) {
                y = otherBottom;
                guides.push({ type: 'horizontal', position: otherBottom });
            } else if (Math.abs(currentBottom - otherTop) < SNAP_THRESHOLD) {
                y = otherTop - height;
                guides.push({ type: 'horizontal', position: otherTop });
            }
        });

        setAlignmentGuides(guides);

        target.style.transform = `translate(${x}px, ${y}px)`;
        target.setAttribute('data-x', x);
        target.setAttribute('data-y', y);

        // Mettre à jour les données de l'élément
        setElements(prev => prev.map(el =>
            el.id === id ? { ...el, x, y } : el
        ));
    };

    const resizeMoveListener = (event) => {
        const target = event.target;
        let x = parseFloat(target.getAttribute('data-x')) || 0;
        let y = parseFloat(target.getAttribute('data-y')) || 0;

        target.style.width = event.rect.width + 'px';
        target.style.height = event.rect.height + 'px';

        x += event.deltaRect.left;
        y += event.deltaRect.top;

        target.style.transform = `translate(${x}px, ${y}px)`;
        target.setAttribute('data-x', x);
        target.setAttribute('data-y', y);

        // Mettre à jour les données de l'élément
        const id = parseInt(target.dataset.id);
        setElements(prev => prev.map(el =>
            el.id === id ? { ...el, x, y, width: event.rect.width, height: event.rect.height } : el
        ));
    };

    useEffect(() => {
        // Configuration d'Interact.js pour le drag, drop et resize
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Permettre le drop sur le canvas
        interact(canvas).dropzone({
            accept: '.draggable-item',
            ondrop: (event) => {
                const type = event.relatedTarget.dataset.type;
                const rect = canvas.getBoundingClientRect();
                const x = event.dragEvent.clientX - rect.left;
                const y = event.dragEvent.clientY - rect.top;

                addElement(type, x, y);
            }
        });

        return () => {
            interact(canvas).unset();
        };
    }, [canvasRef, addElement]);

    useEffect(() => {
        // Configuration pour les éléments sur le canvas - initialiser seulement les nouveaux
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Pour chaque élément, initialiser interact.js seulement s'il n'est pas déjà initialisé
        elements.forEach(element => {
            // Skip si déjà initialisé
            if (interactInitialized.current.has(element.id)) {
                return;
            }

            const el = elementRefs.current[element.id];
            if (!el) {
                return;
            }

            // Marquer comme initialisé
            interactInitialized.current.add(element.id);

            // Initialiser interact
            interact(el)
                .draggable({
                    inertia: false,
                    modifiers: [
                        interact.modifiers.restrictRect({
                            restriction: canvas,
                            endOnly: true
                        })
                    ],
                    autoScroll: false,
                    listeners: {
                        start: (event) => {
                            event.target.classList.add('dragging');
                        },
                        move: dragMoveListener,
                        end: (event) => {
                            event.target.classList.remove('dragging');
                            setAlignmentGuides([]); // Effacer les guides à la fin du drag
                        }
                    }
                })
                .resizable({
                    edges: { left: true, right: true, bottom: true, top: true },
                    listeners: {
                        start: (event) => {
                            event.target.classList.add('resizing');
                        },
                        move: resizeMoveListener,
                        end: (event) => {
                            event.target.classList.remove('resizing');
                        }
                    },
                    modifiers: [
                        interact.modifiers.restrictSize({
                            min: { width: 50, height: 30 }
                        })
                    ]
                });
        });

        return () => {
            // Nettoyer seulement les éléments qui ont été supprimés
            const currentIds = new Set(elements.map(e => e.id));
            const toRemove = Array.from(interactInitialized.current).filter(id => !currentIds.has(id));

            toRemove.forEach(id => {
                const el = elementRefs.current[id];
                if (el) {
                    interact(el).unset();
                }
                interactInitialized.current.delete(id);
                delete elementRefs.current[id];
            });
        };
    }, [elements, canvasRef]);

    useEffect(() => {
        // Configuration pour les éléments draggables de la palette
        interact('.draggable-item').draggable({
            inertia: false,
            autoScroll: true,
            listeners: {
                start: (event) => {
                    event.target.classList.add('is-dragging');
                    event.target.style.zIndex = '99999';
                    event.target.style.position = 'relative';
                },
                move: (event) => {
                    const target = event.target;
                    const x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
                    const y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;

                    target.style.transform = `translate(${x}px, ${y}px)`;
                    target.setAttribute('data-x', x);
                    target.setAttribute('data-y', y);
                },
                end: (event) => {
                    event.target.classList.remove('is-dragging');
                    event.target.style.zIndex = '';
                    event.target.style.position = '';
                    event.target.style.transform = 'translate(0px, 0px)';
                    event.target.setAttribute('data-x', 0);
                    event.target.setAttribute('data-y', 0);
                }
            }
        });

        return () => {
            interact('.draggable-item').unset();
        };
    }, []);

    return { alignmentGuides, elementRefs };
};
